import type { MetaFunction, LoaderFunction } from "@remix-run/node";
import { json, useFetcher, useLoaderData } from "@remix-run/react";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Books, HomeCatalog } from "~/sections";
import { SliderSkeleton } from "~/components";
import type { BlocksResponse, IBlock } from "~/utils/interface";
import { IServerBlocksResponse } from "~/utils/interface";
import { sendToDataLayer } from "~/utils/helpers";
import { useWindowWidth } from "~/utils/hooks/useWindowWidth";

export const meta: MetaFunction = () => [
  { title: "Tempt - Catalog" },
  {
    name: "description",
    content: "Your #1 Romance Audiobook App with Unlimited Listening",
  },
];

export const loader: LoaderFunction = async () => {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}books/selections/web?page=0`,
    );
    const data = await response.json();

    return json({
      initialBlocks: data.blocks || [],
      totalBlocks: data.total_blocks || 0,
    });
  } catch (error) {
    console.error("Failed to fetch initial blocks:", error);
    throw new Response("Failed to load catalog data", { status: 500 });
  }
};

export default function Catalog() {
  const width = useWindowWidth();
  const { initialBlocks, totalBlocks } = useLoaderData<BlocksResponse>();

  const fetcher = useFetcher<IServerBlocksResponse>();
  const observerRef = useRef<HTMLDivElement | null>(null);
  const observerInstance = useRef<IntersectionObserver | null>(null);

  const [blocks, setBlocks] = useState<IBlock[]>(initialBlocks);
  const currentPage = useRef(0);
  const loading = fetcher.state === "loading";

  const loadMoreBlocks = useCallback(() => {
    if (loading || blocks.length >= totalBlocks) return;

    const nextPage = currentPage.current + 1;
    currentPage.current = nextPage;
    fetcher.load(`/api/books?page=${nextPage}`);
  }, [loading, blocks.length, totalBlocks, fetcher]);

  useEffect(() => {
    if (fetcher.data?.blocks) {
      setBlocks((prevBlocks) => [...prevBlocks, ...fetcher.data.blocks]);
      sendToDataLayer({
        event: "selection_displayed",
        property: `page_${fetcher.data.page}`,
      });
    }
  }, [fetcher.data]);

  const setObserverRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerInstance.current)
        if ("disconnect" in observerInstance.current) {
          observerInstance.current.disconnect();
        }
      if (node) {
        observerInstance.current = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              loadMoreBlocks();
            }
          },
          { threshold: 0.5 },
        );
        if ("observe" in observerInstance.current) {
          observerInstance.current.observe(node);
        }
      }
      observerRef.current = node;
    },
    [loadMoreBlocks],
  );

  return (
    <>
      <HomeCatalog />
      <Books blocks={blocks} />
      <section ref={setObserverRef} className="loading-trigger">
        <div className="h-10" />
        {loading && (
          <div className="container">
            <SliderSkeleton count={width > 768 ? 6 : 2} />
            <SliderSkeleton count={width > 768 ? 6 : 2} />
            <SliderSkeleton count={width > 768 ? 6 : 2} />
          </div>
        )}
      </section>
    </>
  );
}
